import { brl } from '../../lib/format'
import { color, radius } from '../../theme/tokens'
import type { Case } from '../../lib/types'
import { Shield, Star } from '../ui'

/** Fila de discagem. O caso ativo aparece em "Conectados"; o resto em "Próximos". */
export function QueuePanel({
  cases,
  activeCaseId,
  queueState,
  onSelect,
}: {
  cases: Case[]
  activeCaseId: string
  queueState: string
  onSelect(caseId: string): void
}) {
  const active = cases.find((item) => item.id === activeCaseId)
  const upcoming = cases.filter((item) => item.id !== activeCaseId)

  return (
    <aside
      style={{
        width: 272,
        flex: 'none',
        background: color.surface,
        borderRight: `1px solid ${color.border}`,
        padding: '14px 12px',
        overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <select
          aria-label="Lista de cobrança"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontWeight: 500,
            padding: '8px 6px',
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            background: color.surface,
          }}
        >
          <option>Lista de Cobrança — Terça</option>
          <option>Carteira Premium 31–60d</option>
        </select>
      </div>

      <input
        placeholder="Buscar contato"
        aria-label="Buscar contato"
        style={{
          width: '100%',
          padding: '9px 12px',
          border: `1px solid ${color.border}`,
          borderRadius: radius.md,
          fontSize: 12.5,
          marginBottom: 10,
        }}
      />

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {['Todos', 'Prontos', 'Discando'].map((label, index) => (
          <span
            key={label}
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 13px',
              borderRadius: radius.pill,
              background: index === 0 ? color.brandHover : color.brandTint,
              color: index === 0 ? '#fff' : color.ink,
              cursor: 'pointer',
            }}
          >
            {label}
          </span>
        ))}
      </div>

      <SectionHeading count={active ? 1 : 0}>Conectados</SectionHeading>
      {active ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '9px 8px',
            borderRadius: radius.md,
            background: color.blueTint,
            border: `1px solid ${color.blueBorder}`,
            marginBottom: 14,
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: radius.md,
              background: color.amberTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              color: color.amberDeep,
            }}
          >
            {active.vip ? <Star size={15} /> : <Shield size={15} />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{active.name}</b>
            <small
              style={{
                color: color.inkSubtle,
                fontSize: 11,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {active.phone} · {active.segment}
            </small>
          </div>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 10.5,
              fontWeight: 700,
              color: color.greenInk,
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: color.green,
                animation: 'lv 1.4s ease-in-out infinite',
              }}
            />
            {queueState}
          </span>
        </div>
      ) : null}

      <SectionHeading count={upcoming.length}>Próximos</SectionHeading>
      {upcoming.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '10px 8px',
            borderBottom: `1px solid ${color.border}`,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            borderBottomStyle: 'solid',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: radius.md,
              background: item.legal_process_active
                ? color.purpleTint
                : item.days_overdue > 60
                  ? color.redTint
                  : color.greenTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            {item.legal_process_active ? (
              <span style={{ color: color.purple, display: 'flex' }}>
                <Shield size={15} />
              </span>
            ) : (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: item.days_overdue > 60 ? color.redLive : color.greenBright,
                }}
              />
            )}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{item.name}</b>
            {item.legal_process_active ? (
              <small style={{ color: color.purple, fontSize: 11, fontWeight: 600 }}>
                Processo jurídico ativo
              </small>
            ) : (
              <small
                style={{
                  color: color.inkSubtle,
                  fontSize: 11,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {brl(item.total_debt)} · {item.days_overdue} dias
              </small>
            )}
          </div>
        </button>
      ))}

      <div
        style={{
          marginTop: 16,
          border: `1px solid ${color.border}`,
          borderRadius: radius.lg,
          padding: '10px 12px',
          background: color.surfaceMuted,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '.06em',
            color: color.inkSubtle,
            marginBottom: 6,
          }}
        >
          INTEGRAÇÃO
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600 }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: color.green }} />
          Salesforce · sincronizado
        </div>
      </div>
    </aside>
  )
}

function SectionHeading({ children, count }: { children: React.ReactNode; count: number }) {
  return (
    <h4
      style={{
        fontSize: 12.5,
        fontWeight: 700,
        margin: '0 0 8px',
        padding: '9px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: color.brandTint,
        borderRadius: radius.md,
      }}
    >
      {children}
      <span
        style={{
          background: color.brandDeep,
          color: '#fff',
          width: 20,
          height: 20,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
        }}
      >
        {count}
      </span>
    </h4>
  )
}
